export const buildUploadItemsFromFiles = ({
  prevItems = [],
  newFileList = [],
}) => {
  const prevByUid = new Map((prevItems || []).map((it) => [it.uid, it]));
  return (newFileList || []).map((file) => {
    const uid = file.uid;
    const existed = prevByUid.get(uid);
    const name = file.name || existed?.name || '';
    const base = name.replace(/\.[^.]+$/, '');
    return {
      uid,
      file,
      name,
      title: existed?.title ?? base,
      summary: existed?.summary ?? '',
      tags: existed?.tags ?? [],
      text_source: existed?.text_source ?? 'asr',
      ocr_max_frames: existed?.ocr_max_frames ?? 6,
      text_source_customized: existed?.text_source_customized ?? false,
      aiStatus: existed?.aiStatus ?? 'idle',
      aiError: existed?.aiError ?? '',
    };
  });
};

export const resolveDefaultPlatformsForFiles = ({
  currentPlatforms,
  newFileList,
  sanitizeDeliveryPlatforms,
  isVideoFileName,
  defaultVideoPlatforms,
}) => {
  const safeCurrentPlatforms = sanitizeDeliveryPlatforms(currentPlatforms);
  const onlyVideos = (newFileList || []).length > 0
    && (newFileList || []).every((file) => isVideoFileName(file?.name || file?.originFileObj?.name));
  if (onlyVideos && safeCurrentPlatforms.length === 0) {
    return defaultVideoPlatforms;
  }
  return safeCurrentPlatforms;
};

export const buildUploadItemFormValues = (item) => ({
  title: item?.title,
  summary: item?.summary,
  tags: item?.tags,
  text_source: item?.text_source,
  ocr_max_frames: item?.ocr_max_frames,
});

export const applyUploadItemFormValues = ({
  items = [],
  selectedUploadUid,
  values = {},
}) => (
  (items || []).map((item) => (
    item.uid === selectedUploadUid
      ? {
        ...item,
        title: values.title || '',
        summary: values.summary || '',
        tags: Array.isArray(values.tags) ? values.tags : [],
        text_source: values.text_source || 'asr',
        ocr_max_frames: values.ocr_max_frames ?? item.ocr_max_frames ?? 6,
      }
      : item
  ))
);

export const applyGlobalTextSourceToItems = ({
  items = [],
  globalTextSource,
  globalOcrMaxFrames,
}) => (
  (items || []).map((item) => (
    item?.text_source_customized
      ? item
      : { ...item, text_source: globalTextSource, ocr_max_frames: globalOcrMaxFrames }
  ))
);

export const buildPreviewMetadataFormData = ({ file, item, existingValues = {} }) => {
  const fd = new FormData();
  fd.append('file', file);
  const chosenTextSource = String(existingValues?.text_source || item?.text_source || 'asr').trim().toLowerCase();
  fd.append('text_source', chosenTextSource);
  if (chosenTextSource === 'image' || chosenTextSource === 'auto') {
    const frames = Number(existingValues?.ocr_max_frames ?? item?.ocr_max_frames ?? 6);
    if (Number.isFinite(frames) && frames > 0) {
      fd.append('ocr_max_frames', String(Math.floor(frames)));
    }
  }
  return fd;
};

export const mergePreviewResult = ({
  response,
  existingValues = {},
  item,
  applyMode = 'all',
}) => {
  const nextTitle = String(response?.title || '').trim();
  const nextSummary = String(response?.summary || '').trim();
  const nextTags = Array.isArray(response?.tags) ? response.tags : [];

  const existingTitle = String(existingValues?.title || '').trim();
  const existingSummary = String(existingValues?.summary || '');
  const existingTags = Array.isArray(existingValues?.tags) ? existingValues.tags : null;

  const fallbackTitle = existingTitle || item?.title || '';
  const fallbackSummary = existingSummary || item?.summary || '';
  const fallbackTags = existingTags || item?.tags || [];

  return {
    title: String(nextTitle || fallbackTitle).trim(),
    summary: applyMode === 'title' ? fallbackSummary : String(nextSummary || fallbackSummary),
    tags: applyMode === 'title' ? fallbackTags : (nextTags.length > 0 ? nextTags : fallbackTags),
  };
};

export const buildUploadFormDataForItem = ({
  values = {},
  item,
  file,
  sanitizeDeliveryPlatforms,
  isVideoFileName,
  defaultVideoPlatforms,
}) => {
  const formData = new FormData();
  if (values.source) formData.append('source', values.source);
  const safePlatforms = sanitizeDeliveryPlatforms(values.available_platforms);
  const itemFileName = item?.name || file?.name || '';
  const platformsForThisItem = safePlatforms.length > 0
    ? safePlatforms
    : (isVideoFileName(itemFileName) ? defaultVideoPlatforms : []);
  if (platformsForThisItem.length > 0) {
    formData.append('available_platforms', platformsForThisItem.join(','));
  }
  if (values.status) formData.append('status', values.status);
  if (item?.title) formData.append('title', item.title);
  if (item?.summary) formData.append('summary', item.summary);
  if (Array.isArray(item?.tags) && item.tags.length > 0) {
    formData.append('tags', item.tags.join(','));
  }
  formData.append('auto_generate', 'false');
  formData.append('files', file);
  return formData;
};
